import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { FileText, Download, Loader2 } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const addHeader = (doc, title, period) => {
  doc.setFontSize(18);
  doc.setTextColor(22, 58, 46);
  doc.text('QUANTORA ANALYTICS PRIVATE LIMITED', 105, 20, { align: 'center' });
  doc.setFontSize(10);
  doc.setTextColor(81, 99, 92);
  doc.text('quantora.co.in', 105, 27, { align: 'center' });
  doc.setDrawColor(226, 223, 216);
  doc.line(20, 30, 190, 30);
  doc.setFontSize(14);
  doc.setTextColor(13, 27, 23);
  doc.text(title, 20, 40);
  doc.setFontSize(9);
  doc.setTextColor(81, 99, 92);
  doc.text(`Period: ${period || 'All Time'} | Generated: ${new Date().toLocaleString()}`, 20, 47);
  return 52;
};

const addSummaryTable = (doc, y, data) => {
  doc.autoTable({
    startY: y, margin: { left: 20, right: 20 },
    head: [['Metric', 'Value']],
    body: data.map(([k, v]) => [k, v]),
    headStyles: { fillColor: [22, 58, 46], fontSize: 9, cellPadding: 4 },
    bodyStyles: { fontSize: 9, cellPadding: 3 },
    alternateRowStyles: { fillColor: [247, 245, 242] },
  });
  return doc.lastAutoTable.finalY + 8;
};

export default function PdfReportsPage() {
  const [clients, setClients] = useState([]);
  const [branches, setBranches] = useState([]);
  const [traders, setTraders] = useState([]);
  const [managers, setManagers] = useState([]);
  const [period, setPeriod] = useState('');
  const [generating, setGenerating] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/clients').catch(() => ({ data: [] })),
      api.get('/branches').catch(() => ({ data: [] })),
      api.get('/users', { params: { role: 'trader' } }).catch(() => ({ data: [] })),
      api.get('/users', { params: { role: 'manager' } }).catch(() => ({ data: [] })),
    ]).then(([c, b, t, m]) => { setClients(c.data); setBranches(b.data); setTraders(t.data); setManagers(m.data); });
  }, []);

  const generateClientPDF = async (clientId) => {
    setGenerating('client-' + clientId);
    try {
      const { data } = await api.get(`/pdf-reports/client-statement/${clientId}`, { params: period ? { period } : {} });
      const doc = new jsPDF();
      let y = addHeader(doc, `Client Statement - ${data.client?.name}`, data.period);
      const s = data.summary;
      y = addSummaryTable(doc, y, [
        ['Total Deposits', `$${s.total_deposits.toLocaleString()}`],
        ['Total Withdrawals', `$${s.total_withdrawals.toLocaleString()}`],
        ['Total P&L', `$${s.total_pnl.toLocaleString()}`],
        [`Your Profit Share (${s.investor_pct}%)`, `$${s.client_profit_share.toLocaleString()}`],
        [`Company Revenue (${s.company_pct}%)`, `$${s.company_revenue_share.toLocaleString()}`],
        ['Main Balance', `$${s.withdrawable_balance.toLocaleString()}`],
      ]);
      if (data.transactions?.length) {
        doc.setFontSize(11); doc.setTextColor(13, 27, 23); doc.text('Transaction History', 20, y); y += 5;
        doc.autoTable({
          startY: y, margin: { left: 20, right: 20 },
          head: [['Date', 'Type', 'Amount', 'Status']],
          body: data.transactions.map(t => [
            t.created_at ? new Date(t.created_at).toLocaleDateString() : '-',
            t.type?.replace('_', ' '), `$${Number(t.amount).toLocaleString()}`, t.status
          ]),
          headStyles: { fillColor: [22, 58, 46], fontSize: 8, cellPadding: 3 },
          bodyStyles: { fontSize: 8, cellPadding: 2 },
          alternateRowStyles: { fillColor: [247, 245, 242] },
        });
      }
      doc.save(`client-statement-${data.client?.name?.replace(/\s+/g, '-')}.pdf`);
      toast.success('PDF downloaded');
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setGenerating(''); }
  };

  const generateBranchPDF = async (branchId) => {
    setGenerating('branch-' + branchId);
    try {
      const { data } = await api.get(`/pdf-reports/branch-report/${branchId}`, { params: period ? { period } : {} });
      const doc = new jsPDF();
      let y = addHeader(doc, `Branch Report - ${data.branch?.name}`, data.period);
      y = addSummaryTable(doc, y, [
        ['Total Clients', data.summary.total_clients], ['Total Traders', data.summary.total_traders],
        ['Total Profit', `$${data.summary.total_profit.toLocaleString()}`], ['Total Loss', `$${data.summary.total_loss.toLocaleString()}`],
        ['Net P&L', `$${data.summary.net_pnl.toLocaleString()}`],
      ]);
      if (data.clients?.length) {
        doc.setFontSize(11); doc.text('Client Breakdown', 20, y); y += 5;
        doc.autoTable({
          startY: y, margin: { left: 20, right: 20 },
          head: [['Client', 'Net P&L', 'Status']], body: data.clients.map(c => [c.name, `$${c.net_pnl.toLocaleString()}`, c.status]),
          headStyles: { fillColor: [22, 58, 46], fontSize: 8, cellPadding: 3 }, bodyStyles: { fontSize: 8, cellPadding: 2 },
        });
      }
      doc.save(`branch-report-${data.branch?.name?.replace(/\s+/g, '-')}.pdf`);
      toast.success('PDF downloaded');
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setGenerating(''); }
  };

  const generateTraderPDF = async (traderId) => {
    setGenerating('trader-' + traderId);
    try {
      const { data } = await api.get(`/pdf-reports/trader-report/${traderId}`, { params: period ? { period } : {} });
      const doc = new jsPDF();
      let y = addHeader(doc, `Trader Report - ${data.trader?.name}`, data.period);
      y = addSummaryTable(doc, y, [
        ['Total Income', `$${data.summary.total_income.toLocaleString()}`],
        ['Income Entries', data.summary.entries], ['Assigned Clients', data.summary.assigned_clients],
      ]);
      if (data.income_records?.length) {
        doc.setFontSize(11); doc.text('Income Records', 20, y); y += 5;
        doc.autoTable({
          startY: y, margin: { left: 20, right: 20 },
          head: [['Date', 'Amount', 'Client', 'Source']], body: data.income_records.map(i => [i.date, `$${Number(i.amount).toLocaleString()}`, i.client_name || '-', i.source]),
          headStyles: { fillColor: [22, 58, 46], fontSize: 8, cellPadding: 3 }, bodyStyles: { fontSize: 8, cellPadding: 2 },
        });
      }
      doc.save(`trader-report-${data.trader?.name?.replace(/\s+/g, '-')}.pdf`);
      toast.success('PDF downloaded');
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setGenerating(''); }
  };

  const generateManagerPDF = async (managerId) => {
    setGenerating('manager-' + managerId);
    try {
      const { data } = await api.get(`/pdf-reports/manager-report/${managerId}`, { params: period ? { period } : {} });
      const doc = new jsPDF();
      let y = addHeader(doc, `Manager Report - ${data.manager?.name}`, data.period);
      y = addSummaryTable(doc, y, [
        ['Branch', data.branch?.name || '-'], ['Total Incentive', `$${data.summary.total_incentive.toLocaleString()}`], ['Records', data.summary.records],
      ]);
      if (data.incentives?.length) {
        doc.autoTable({
          startY: y, margin: { left: 20, right: 20 },
          head: [['Period', 'Branch Profit', 'Incentive %', 'Amount', 'Status']],
          body: data.incentives.map(i => [i.period, `$${Number(i.branch_profit).toLocaleString()}`, `${i.incentive_pct}%`, `$${Number(i.incentive_amount).toLocaleString()}`, i.status]),
          headStyles: { fillColor: [22, 58, 46], fontSize: 8, cellPadding: 3 }, bodyStyles: { fontSize: 8, cellPadding: 2 },
        });
      }
      doc.save(`manager-report-${data.manager?.name?.replace(/\s+/g, '-')}.pdf`);
      toast.success('PDF downloaded');
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setGenerating(''); }
  };

  const generateEmployeePDF = async () => {
    setGenerating('employee');
    try {
      const { data } = await api.get('/pdf-reports/employee-report', { params: period ? { period } : {} });
      const doc = new jsPDF();
      let y = addHeader(doc, 'Employee Salary Report', data.period);
      y = addSummaryTable(doc, y, [['Total Paid', `$${data.total_paid.toLocaleString()}`], ['Total Pending', `$${data.total_pending.toLocaleString()}`]]);
      if (data.employees?.length) {
        doc.autoTable({
          startY: y, margin: { left: 20, right: 20 },
          head: [['Name', 'Role', 'Email', 'Paid', 'Pending']],
          body: data.employees.map(e => [e.name, e.role, e.email, `$${e.total_paid.toLocaleString()}`, `$${e.total_pending.toLocaleString()}`]),
          headStyles: { fillColor: [22, 58, 46], fontSize: 8, cellPadding: 3 }, bodyStyles: { fontSize: 8, cellPadding: 2 },
        });
      }
      doc.save('employee-salary-report.pdf');
      toast.success('PDF downloaded');
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setGenerating(''); }
  };

  const BtnGen = ({ id, label, onClick }) => (
    <Button onClick={onClick} disabled={generating === id} variant="outline" size="sm" className="rounded-sm text-xs border-brand-border" data-testid={`gen-${id}`}>
      {generating === id ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Download size={12} className="mr-1" />} {label}
    </Button>
  );

  return (
    <div className="space-y-6" data-testid="pdf-reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Generate Reports</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Download PDF reports: client, branch, trader, manager, employee</p>
        </div>
        <div className="space-y-1"><Label className="text-xs">Period (optional)</Label><Input type="month" value={period} onChange={e => setPeriod(e.target.value)} className="bg-white border-brand-border rounded-sm w-44" data-testid="report-period" /></div>
      </div>

      <Tabs defaultValue="client" className="space-y-4">
        <TabsList className="bg-white border border-brand-border rounded-sm p-1 flex-wrap gap-1">
          {[{ v: 'client', l: 'Client' }, { v: 'branch', l: 'Branch' }, { v: 'trader', l: 'Trader' }, { v: 'manager', l: 'Manager' }, { v: 'employee', l: 'Employee' }].map(t => (
            <TabsTrigger key={t.v} value={t.v} className="rounded-sm text-xs data-[state=active]:bg-brand-primary data-[state=active]:text-white px-3 py-1.5">{t.l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="client"><Card className="border border-brand-border bg-white"><CardContent className="p-0"><div className="overflow-x-auto">
          <table className="w-full text-sm"><thead><tr className="border-b border-brand-border bg-brand-bg/50">
            {['Client', 'Email', 'Status', 'Action'].map(h => <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>)}
          </tr></thead><tbody>
            {clients.length === 0 ? <tr><td colSpan={4} className="py-8 text-center text-brand-text-secondary">No clients</td></tr> :
             clients.map(c => (
              <tr key={c.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover"><td className="py-2 px-4 text-brand-text-primary text-sm">{c.name}</td><td className="py-2 px-4 text-brand-text-secondary text-xs">{c.email}</td>
                <td className="py-2 px-4 text-xs">{c.status}</td><td className="py-2 px-4"><BtnGen id={`client-${c.id}`} label="Statement PDF" onClick={() => generateClientPDF(c.id)} /></td></tr>))}
          </tbody></table></div></CardContent></Card></TabsContent>

        <TabsContent value="branch"><Card className="border border-brand-border bg-white"><CardContent className="p-0"><div className="overflow-x-auto">
          <table className="w-full text-sm"><thead><tr className="border-b border-brand-border bg-brand-bg/50">
            {['Branch', 'City', 'Action'].map(h => <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>)}
          </tr></thead><tbody>
            {branches.length === 0 ? <tr><td colSpan={3} className="py-8 text-center text-brand-text-secondary">No branches</td></tr> :
             branches.map(b => (
              <tr key={b.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover"><td className="py-2 px-4 text-brand-text-primary text-sm">{b.name}</td><td className="py-2 px-4 text-brand-text-secondary text-xs">{b.city || '-'}</td>
                <td className="py-2 px-4"><BtnGen id={`branch-${b.id}`} label="Branch Report PDF" onClick={() => generateBranchPDF(b.id)} /></td></tr>))}
          </tbody></table></div></CardContent></Card></TabsContent>

        <TabsContent value="trader"><Card className="border border-brand-border bg-white"><CardContent className="p-0"><div className="overflow-x-auto">
          <table className="w-full text-sm"><thead><tr className="border-b border-brand-border bg-brand-bg/50">
            {['Trader', 'Email', 'Action'].map(h => <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>)}
          </tr></thead><tbody>
            {traders.length === 0 ? <tr><td colSpan={3} className="py-8 text-center text-brand-text-secondary">No traders</td></tr> :
             traders.map(t => (
              <tr key={t.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover"><td className="py-2 px-4 text-brand-text-primary text-sm">{t.name}</td><td className="py-2 px-4 text-brand-text-secondary text-xs">{t.email}</td>
                <td className="py-2 px-4"><BtnGen id={`trader-${t.id}`} label="Trader Report PDF" onClick={() => generateTraderPDF(t.id)} /></td></tr>))}
          </tbody></table></div></CardContent></Card></TabsContent>

        <TabsContent value="manager"><Card className="border border-brand-border bg-white"><CardContent className="p-0"><div className="overflow-x-auto">
          <table className="w-full text-sm"><thead><tr className="border-b border-brand-border bg-brand-bg/50">
            {['Manager', 'Email', 'Action'].map(h => <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>)}
          </tr></thead><tbody>
            {managers.length === 0 ? <tr><td colSpan={3} className="py-8 text-center text-brand-text-secondary">No managers</td></tr> :
             managers.map(m => (
              <tr key={m.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover"><td className="py-2 px-4 text-brand-text-primary text-sm">{m.name}</td><td className="py-2 px-4 text-brand-text-secondary text-xs">{m.email}</td>
                <td className="py-2 px-4"><BtnGen id={`manager-${m.id}`} label="Manager Report PDF" onClick={() => generateManagerPDF(m.id)} /></td></tr>))}
          </tbody></table></div></CardContent></Card></TabsContent>

        <TabsContent value="employee"><Card className="border border-brand-border bg-white"><CardContent className="p-6 text-center space-y-4">
          <FileText size={48} className="text-brand-text-secondary mx-auto" strokeWidth={1} />
          <p className="text-sm text-brand-text-secondary">Generate a complete employee salary report for the selected period</p>
          <BtnGen id="employee" label="Download Employee Salary Report" onClick={generateEmployeePDF} />
        </CardContent></Card></TabsContent>
      </Tabs>
    </div>
  );
}
